// @ts-nocheck
import { Box, Container, createMuiTheme, IconButton, LabelDisplayedRowsArgs, MuiThemeProvider, TableBody, TableCell, TableFooter, TablePagination, TableRow, Theme, Tooltip } from '@material-ui/core'
import TablePaginationActions from '@material-ui/core/TablePagination/TablePaginationActions'
import AddIcon from '@material-ui/icons/Add'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { Loading } from 'components/loading/Loading'
import MUIDataTable, { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables'
import PropTypes from 'prop-types'
import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FixMeLater } from 'types/FixMeLaterType'


interface TableV2Props<T> {
    data: T[],
    dataKeyField?: string | undefined,
    columnFields: MUIDataTableColumn[],
    downloadFilename?: string | undefined,
    handleOnRowClick: (item: T) => void,
    handleOnRowsDelete: (items: string[]) => void,
    handleAdd: () => void,
    loading?: boolean | null
    addText?: string | undefined,
    handlePageChange?: ((page: number) => void) | null,
    handleRowPerPageChange?: ((pageSize: number) => void) | null,
    overridePageRowOptions?: number[] | null,
    searchDisabled?: boolean | null,
    filterDisabled?: boolean | null,
    title: string,
    overridePage?: number | null,
    overrideRowsPerPage?: number | null,
    overrideDisplayedRows?: ((to: number, from: number, count: number, page: number) => string) | null
    hasMorePages?: boolean | null,
    customActions?: typeof TablePaginationActions | null,
    overrideFilter?: ((changedColumn: string, filterList: any[], type: 'checkbox' | 'dropdown' | 'multiselect' | 'textField' | 'custom' | 'chip' | 'reset', changedColumnIndex: number, displayData: any[]) => void) | null
}

const useStyles = makeStyles((theme: Theme) => ({
    footerCell: {
        backgroundColor: theme.palette.background.paper,
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    },
    stickyFooterCell: {
        padding: "0",
    },
    table: {
        height: "100%",
        position: "relative"
    },
    tablePagination: {
        height: "52px"
    },
    loadingContainer: {
        position: "absolute",
        left: 0,
        right: 0
    }
}))

function TableV2<T extends { [index: string]: any }>(props: TableV2Props<T>) {

    const [t, i18n] = useTranslation()
    const classes = useStyles()

    const [filtering, setFiltering] = useState(false)
    const [selected, setSelected] = useState<any[]>([])

    const footerClasses = clsx({
        [classes.footerCell]: true,
        [classes.stickyFooterCell]: true,
    });

    const dataKeyField = useMemo(() => props.dataKeyField || "id", [props.dataKeyField])

    const rowsPerPageOptions = useMemo(() => props.overridePageRowOptions || [25, 50, 100], [props.overridePageRowOptions])

    /**
     * Filter change callback to filtered data
     * @param changedColumn Name of column changed
     * @param filterList List of filter lists for each column
     * @param type Type of filter component
     * @param changedColumnIndex Index of column changed
     * @param displayData Table data displayed
     */
    const onFilterChanged = useCallback(
        (
            changedColumn: string,
            filterList: string[][],
            type: 'checkbox' | 'dropdown' | 'multiselect' | 'textField' | 'custom' | 'chip' | 'reset',
            changedColumnIndex: number,
            displayData: any
        ) => {
            var filtersExist = false
            var filters = filterList[changedColumnIndex]

            // Determine if we still have currently active filters.
            filtersExist = filters?.length > 0 ? true : false;

            if (!!props.overrideFilter) {
                props.overrideFilter(changedColumn, filterList, type, changedColumnIndex, displayData)
            }
            setFiltering(filtersExist)
            setSelected([])
        },
        [props.overrideFilter, setFiltering, setSelected],
    )

    /**
     * Row Click callback
     * @param rowData Column data for row
     * @param rowMeta Column data indicies for row
     */
    const onRowClick = useCallback(
        (rowData: string[], rowMeta: { dataIndex: number, rowIndex: number }) => {
            const dataItem: T = props.data[rowMeta.dataIndex]
            props.handleOnRowClick(dataItem)
        },
        [props.handleOnRowClick, props.data],
    )

    /**
     * Delete callback for selected rows
     * @param rowsDeleted List of deleted data
     * @param newTableData Updated table data list
     * @returns False to stop visual removal from table
     */
    const onRowsDelete = useCallback(
        (rowsDeleted: {
            lookup: { [dataIndex: number]: boolean };
            data: { index: number; dataIndex: number }[];
        }, newTableData: any[]): false | void => {
            // Add to a managed list of items to delete. maybe open delete confirmation dialog
            const deleteList: string[] = []
            rowsDeleted.data.forEach(({ index, dataIndex }: { index: number, dataIndex: number }
            ) => {
                const deleteItem: T = props.data[dataIndex]
                if (!!deleteItem) {
                    deleteList.push(deleteItem[dataKeyField])
                }
            });
            props.handleOnRowsDelete(deleteList)
            setSelected([])
            return false
        },
        [props.handleOnRowsDelete, setSelected, props.data],
    )

    /**
     * Custom Toolbar Component
     * @returns Custom Toolbar Component
     */
    const customToolbar = useCallback(
        () => (
            <Fragment>
                <Tooltip
                    title={props.addText || t('common.add') || ""}
                >
                    <IconButton onClick={props.handleAdd}>
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </Fragment >
        ),
        [props.handleAdd, props.addText, t],
    )


    const onRowSelectionChange = useCallback(
        /**
         * Selection update handler for rows
         * @param currentRowsSelected List rows selection has changed for
         * @param allRowsSelected List of metadata for currently selected rows
         * @param rowsSelected List of indicies for currently selected rows
         */
        (currentRowsSelected: any[], allRowsSelected: any[], rowsSelected: any[]) => {
            setSelected(rowsSelected)
        },
        [setSelected],
    )


    /**
     * Render a Custom Footer Component
     * @param count Number of current rows
     * @param page Current page number
     * @param rowsPerPage Current rows per page count
     * @param changeRowsPerPage Function for changing rows per page
     * @param changePage Function for changing page number
     * @param textLabels Map of label strings
     * @returns Custom Footer Component
     */
    const customFooter = useCallback(
        (
            count,
            page,
            rowsPerPage,
            changeRowsPerPage,
            changePage,
            textLabels
        ) => {
            const handleRowCountChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                const pageSize = event.target.value

                if (typeof pageSize != "number") {
                    return
                }

                if (!!props.handleRowPerPageChange) {
                    props.handleRowPerPageChange(pageSize)
                } else {
                    changeRowsPerPage(pageSize);
                }
            };

            const handlePageChange = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, page: number) => {
                if (!!props.handlePageChange) {
                    props.handlePageChange(page)
                } else {
                    changePage(page);
                }
            };

            const renderDisplayedRows = ({ from, to, count, page }: LabelDisplayedRowsArgs) => (
                !!props.overrideDisplayedRows ?
                    props.overrideDisplayedRows(from, to, count, page) :
                    `${from}-${to} ${textLabelPagination?.displayRows} ${count}`
            )

            var textLabelPagination: FixMeLater = !!textLabels?.pagination ? textLabels.pagination : textLabels

            return (
                <TableFooter className={footerClasses}>
                    <TableRow >
                        <TableCell className={footerClasses}>
                            <TablePagination
                                className={classes.tablePagination}
                                component="div"
                                count={props.data.length}
                                rowsPerPage={!!props.overrideRowsPerPage ? props.overrideRowsPerPage : rowsPerPage}
                                page={props.overridePage != null ? props.overridePage : page}
                                labelRowsPerPage={textLabelPagination?.rowsPerPage}
                                labelDisplayedRows={renderDisplayedRows}
                                backIconButtonProps={{
                                    'aria-label': textLabelPagination?.previous,
                                }}
                                nextIconButtonProps={{
                                    'aria-label': textLabelPagination?.next,
                                }}
                                rowsPerPageOptions={rowsPerPageOptions}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handleRowCountChange}
                                ActionsComponent={!!props.customActions ? props.customActions : undefined}
                            />
                        </TableCell>
                    </TableRow>
                </TableFooter >
            );
        },
        [props.handleRowPerPageChange, props.handlePageChange, props.data, props.overrideRowsPerPage, props.overridePage, props.overrideDisplayedRows, rowsPerPageOptions, props.customActions]
    )

    /**
     * MUI Datatable options object
     */
    const options: MUIDataTableOptions = {
        elevation: 0,
        responsive: "standard",
        rowsPerPage: !!props.overrideRowsPerPage ? props.overrideRowsPerPage : rowsPerPageOptions[0],
        rowsPerPageOptions: rowsPerPageOptions,
        viewColumns: false,
        filter: !props.filterDisabled,
        filterType: "checkbox",
        print: false,
        search: !props.searchDisabled,
        download: false,
        selectableRows: "multiple",
        rowHover: true,
        tableBodyHeight: `calc(100% - ${filtering ? 156 : 116}px)`,
        downloadOptions: !!props.downloadFilename ? {
            filename: props.downloadFilename,
            separator: ",",
            filterOptions: {
                useDisplayedColumnsOnly: true,
                useDisplayedRowsOnly: true
            }
        } : undefined,
        onFilterChange: onFilterChanged,
        onRowClick: onRowClick,
        onRowsDelete: onRowsDelete,
        customToolbar: customToolbar,
        onRowSelectionChange: onRowSelectionChange,
        rowsSelected: selected,
        customFooter: customFooter
    }

    const getMuiTheme = () => createMuiTheme({
        overrides: {
            //@ts-ignore
            MUIDataTableFooter: {
                root: {
                    position: 'absolute',
                    marginTop: "-1px",
                    right: 0,
                    left: 0,
                    bottom: 0,
                    alignItems: "end",
                    padding: "0",
                    zIndex: 100,
                }
            },

            MUIDataTable: {
                responsiveBase: {
                    background:
                        "linear-gradient(white 30%, rgba(255, 255, 255, 0)), " +
                        "linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%, " +
                        "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0) 100%)",
                    backgroundRepeat: "no-repeat",
                    backgroundColor: "white",
                    backgroundSize: "100% 40px, 100% 40px, 100% 14px, 100% 14px",
                    backgroundAttachment: "local, local, scroll, scroll",
                    backgroundPosition: "left 0px top 56px"
                },
                paper: {
                    height: "inherit"
                }
            },

            MuiTableCell: {
                body: {
                    color: "#4C73FF",
                    fontWeight: 400,
                    cursor: "pointer",
                }
            }
        }
    })

    const loadingContainer = () => {
        return (
            <Box className={classes.loadingContainer}>
                <Loading />
            </Box>
        )
    }

    return (
        <Box className={classes.table}>
            <MuiThemeProvider theme={getMuiTheme()} >
                <MUIDataTable
                    title={props.title}
                    data={props.data}
                    columns={props.columnFields}
                    options={options}
                    components={
                        !!props.loading ? {
                            TableBody: () => loadingContainer()
                        } : undefined
                    }
                />
            </MuiThemeProvider>
        </Box>
    )
}

TableV2.propTypes = {
    data: PropTypes.array.isRequired,
    dataKeyField: PropTypes.string,
    columnFields: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        field: PropTypes.string.isRequired,
        filterable: PropTypes.bool
    })),
    downloadFilename: PropTypes.string,
    handleOnRowClick: PropTypes.func.isRequired,
    handleOnRowsDelete: PropTypes.func.isRequired,
    handleAdd: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    addText: PropTypes.string,
    handlePageChange: PropTypes.func,
    handleRowPerPageChange: PropTypes.func,
    overridePageRowOptions: PropTypes.arrayOf(PropTypes.number),
    searchDisabled: PropTypes.bool,
    filterDisabled: PropTypes.bool,
    title: PropTypes.string.isRequired,
    overridePage: PropTypes.number,
    overrideRowsPerPage: PropTypes.number,
    overrideDisplayedRows: PropTypes.func,
    hasMorePages: PropTypes.bool,
    customActions: PropTypes.shape({
        backIconButtonProps: PropTypes.shape({
            children: PropTypes.element,
            classes: PropTypes.any,
            color: PropTypes.string,
            disabled: PropTypes.bool,
            disableFocusRipple: PropTypes.bool,
            disableRipple: PropTypes.bool,
            edge: PropTypes.oneOf([PropTypes.string, false]),
            size: PropTypes.string
        }),
        count: PropTypes.number.isRequired,
        nextIconButtonProps: PropTypes.shape({
            children: PropTypes.element,
            classes: PropTypes.any,
            color: PropTypes.string,
            disabled: PropTypes.bool,
            disableFocusRipple: PropTypes.bool,
            disableRipple: PropTypes.bool,
            edge: PropTypes.oneOf([PropTypes.string, false]),
            size: PropTypes.string
        }),
        onChangePage: PropTypes.func.isRequired,
        page: PropTypes.number.isRequired,
        rowsPerPage: PropTypes.number.isRequired
    }),
    overrideFilter: PropTypes.func
}

export default memo(TableV2)

